import React, {useState, useEffect, useRef} from "react"
import {CelebrationTag, CelebrationTagKind} from "@irati/types"
import CelebrationTagsQuery from "@irati/recognition/services/queries/CelebrationTags"
import {Query} from "@irati/utils/services/apollo"
import Slider from "@irati/ui/src/Slider"
import {DESKTOP, PHONE, PHONE_LANDSCAPE, TABLET} from "@irati/ui/src/global/media"
import OccasionsService from "@irati/recognition/services/Occasions"
import Spacing from "@irati/ui/src/Spacing"
import TemplateCarouselOption, {Props} from "./TemplateCarouselOption"

interface TemplatesCarousel {
  onChange: (tag: CelebrationTag) => void
  defaultValue?: string
  hideDefault?: boolean
  sliderSettings?: SliderSettings
  variant?: Props["variant"]
}
interface BaseSliderSettings {
  slidesToShow: number
  slidesToScroll: number
}
interface ResponsiveSetting {
  breakpoint: number
  settings: BaseSliderSettings
}
interface SliderSettings extends BaseSliderSettings {
  responsive: ResponsiveSetting[]
}
const defaultSliderSettings: SliderSettings = {
  slidesToScroll: 2,
  slidesToShow: 5,
  responsive: [
    {breakpoint: PHONE - 1, settings: {slidesToShow: 1.5, slidesToScroll: 1}},
    {breakpoint: PHONE_LANDSCAPE - 1, settings: {slidesToShow: 2.5, slidesToScroll: 1}},
    {breakpoint: TABLET - 1, settings: {slidesToShow: 3, slidesToScroll: 1}},
    {breakpoint: DESKTOP - 1, settings: {slidesToShow: 3, slidesToScroll: 2}}
  ]
}
const TemplatesCarousel = (props: TemplatesCarousel) => {
  const {onChange, defaultValue, variant = "boxed", sliderSettings = defaultSliderSettings, hideDefault} = props
  const [selectedTag, setSelectedTag] = useState<CelebrationTag>()
  const loaded = useRef<boolean>(false)

  const onTagSelected = (tag: CelebrationTag) => {
    setSelectedTag(tag)
    onChange(tag)
  }

  useEffect(() => {
    if (!defaultValue || loaded.current) return
    loaded.current = true
    OccasionsService.getCelebrationsTags(CelebrationTagKind.ThemeTemplate).then(selectDefault)
  }, [defaultValue])

  const selectDefault = (celebrationTags: CelebrationTag[]) => {
    if (!defaultValue || !celebrationTags) {
      return
    }

    const defaultFound = celebrationTags.find(({uuid}) => defaultValue === uuid)

    if (!defaultFound) {
      return
    }

    onTagSelected(defaultFound)
  }

  return (
    <Query query={CelebrationTagsQuery} variables={{kind: CelebrationTagKind.ThemeTemplate}}>
      {({data: {celebrationTags}, loading}) => {
        if (loading || !celebrationTags) {
          return null
        }
        const defaultSlidePosition = celebrationTags.findIndex(({uuid}) => uuid === defaultValue)
        const initialSlide = defaultSlidePosition !== -1 ? defaultSlidePosition + (hideDefault ? 0 : 1) : 0
        return (
          <Spacing marginTop={2}>
            <Slider settings={sliderSettings} maintainSameHeight initialSlide={initialSlide}>
              {!hideDefault && (
                <TemplateCarouselOption
                  tag={{name: "wallet.templates.defaultName"}}
                  isDefault
                  variant={variant}
                  selected={!selectedTag?.uuid}
                  onSelect={() => onTagSelected()}
                />
              )}
              {celebrationTags.map((tag: CelebrationTag) => (
                <TemplateCarouselOption
                  key={tag.uuid}
                  tag={tag}
                  onSelect={onTagSelected}
                  selected={selectedTag?.uuid === tag.uuid}
                  variant={variant}
                />
              ))}
            </Slider>
          </Spacing>
        )
      }}
    </Query>
  )
}
export default TemplatesCarousel
